import { useState, useEffect, useMemo } from "react";
import { usersListUsers } from "../gen";
import type { UserResponse } from "../gen/models";

export const CANONICAL_AGENT_LABELS: Record<string, string> = {
  sales: "Quotes",
  feedback: "Feedback",
  biblioteca: "Analytics",
  frontdesk: "Sofia",
  el_protocolo: "Sofia",
  protocolo: "Sofia",
  email_drafting: "Email Drafter",
};

let cachedUsers: UserResponse[] | null = null;
let fetchPromise: Promise<UserResponse[]> | null = null;

export function sanitizeProtocoloName(
  text?: string | null,
  replacement: string = "Sofia"
): string {
  if (!text) return "";
  return text
    .replace(/\bel\s+protocolo\b/gi, replacement)
    .replace(/\b(el_)?protocolo\b/gi, replacement);
}

export function normalizeAgentType(agentType?: string | null): string {
  if (!agentType) return "";
  const cleaned = agentType.trim().toLowerCase();
  if (cleaned === "el_protocolo" || cleaned === "protocolo") {
    return "frontdesk";
  }
  return cleaned;
}

export async function fetchUserDirectory(): Promise<UserResponse[]> {
  try {
    const res: any = await usersListUsers({ limit: 100 });
    const data = res?.data || res;
    const rawUsers: UserResponse[] = Array.isArray(data)
      ? data
      : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.users)
      ? data.users
      : [];

    cachedUsers = rawUsers;
    return rawUsers;
  } catch (err) {
    if (!cachedUsers) {
      cachedUsers = [];
    }
    return cachedUsers;
  }
}

export interface UserDirectoryResult {
  users: UserResponse[];
  usersById: Map<string, UserResponse>;
  usersByEmail: Map<string, UserResponse>;
  agentsByType: Map<string, UserResponse>;
  loading: boolean;
  getUserDisplayName: (userId?: string | null, fallbackName?: string | null) => string;
  getAgentDisplayName: (agentType?: string | null, fallbackName?: string | null) => string;
  sanitizeProtocoloName: (text?: string | null, replacement?: string) => string;
  normalizeAgentType: (agentType?: string | null) => string;
  resolveSenderDisplayName: (params: {
    senderRole?: string;
    senderId?: string | null;
    senderName?: string | null;
    agentType?: string | null;
  }) => string;
}

export function useUserDirectory(): UserDirectoryResult {
  const [users, setUsers] = useState<UserResponse[]>(() => cachedUsers || []);
  const [loading, setLoading] = useState<boolean>(() => !cachedUsers);

  useEffect(() => {
    let mounted = true;

    if (!cachedUsers) {
      if (!fetchPromise) {
        fetchPromise = fetchUserDirectory();
      }
      fetchPromise
        .then((data) => {
          if (mounted) {
            setUsers(data);
            setLoading(false);
          }
        })
        .finally(() => {
          fetchPromise = null;
        });
    } else {
      setUsers(cachedUsers);
      setLoading(false);
    }

    return () => {
      mounted = false;
    };
  }, []);

  const usersById = useMemo(() => {
    const map = new Map<string, UserResponse>();
    for (const u of users) {
      if (u.user_id) {
        map.set(u.user_id, u);
      }
    }
    return map;
  }, [users]);

  const usersByEmail = useMemo(() => {
    const map = new Map<string, UserResponse>();
    for (const u of users) {
      if (u.email) {
        map.set(u.email.toLowerCase(), u);
      }
    }
    return map;
  }, [users]);

  const agentsByType = useMemo(() => {
    const map = new Map<string, UserResponse>();
    for (const u of users) {
      if (u.is_agent && u.agent_type && u.status !== "inactive") {
        const norm = normalizeAgentType(u.agent_type);
        if (!map.has(norm)) {
          map.set(norm, u);
        }
        map.set(u.agent_type, u);
      }
    }
    return map;
  }, [users]);

  const getUserDisplayName = (
    userId?: string | null,
    fallbackName?: string | null
  ): string => {
    if (userId) {
      if (usersById.has(userId)) {
        const u = usersById.get(userId)!;
        const name = u.display_name || u.effective_name;
        if (name && name.trim()) {
          return sanitizeProtocoloName(name.trim(), "Sofia");
        }
      }
      const uBySlack = users.find((u) => {
        const meta = u.payload_metadata as Record<string, any> | undefined;
        return (
          meta?.slack_user_id === userId ||
          meta?.slack_id === userId ||
          meta?.slack_username === userId
        );
      });
      if (uBySlack) {
        const name = uBySlack.display_name || uBySlack.effective_name;
        if (name && name.trim()) {
          return sanitizeProtocoloName(name.trim(), "Sofia");
        }
      }
    }
    if (fallbackName && fallbackName.trim() && fallbackName.trim() !== userId) {
      return sanitizeProtocoloName(fallbackName.trim(), "Sofia");
    }
    return userId ? "Unresolved User" : "Patron";
  };

  const getAgentDisplayName = (
    agentType?: string | null,
    fallbackName?: string | null
  ): string => {
    const norm = normalizeAgentType(agentType);
    if (agentsByType.has(norm)) {
      const u = agentsByType.get(norm)!;
      const name = u.display_name || u.effective_name;
      if (name && name.trim()) {
        return sanitizeProtocoloName(name.trim(), "Sofia");
      }
    }
    if (norm && CANONICAL_AGENT_LABELS[norm]) {
      return CANONICAL_AGENT_LABELS[norm];
    }
    if (fallbackName && fallbackName.trim()) {
      return sanitizeProtocoloName(fallbackName.trim(), "Sofia");
    }
    return "Autonomous Agent";
  };

  const resolveSenderDisplayName = ({
    senderRole,
    senderId,
    senderName,
    agentType,
  }: {
    senderRole?: string;
    senderId?: string | null;
    senderName?: string | null;
    agentType?: string | null;
  }): string => {
    const isAgent = senderRole === "agent" || senderRole === "assistant";

    if (isAgent) {
      const agentDisplayName = getAgentDisplayName(agentType, senderName);
      const isSim = Boolean(
        senderName &&
          (/simulation/i.test(senderName) ||
            /simulado/i.test(senderName) ||
            senderName.toLowerCase().includes("(simulation)"))
      );

      if (isSim) {
        return `${agentDisplayName} (Simulation)`;
      }

      if (senderName && !/protocolo/i.test(senderName) && senderName !== agentType) {
        return senderName.trim();
      }

      return agentDisplayName;
    }

    // Human / staff / visitor / patron message
    if (senderId && usersById.has(senderId)) {
      const u = usersById.get(senderId)!;
      const name = u.display_name || u.effective_name;
      if (name && name.trim()) {
        return sanitizeProtocoloName(name.trim(), "Sofia");
      }
    }

    if (senderName && senderName.trim()) {
      const lower = senderName.trim().toLowerCase();
      if (usersByEmail.has(lower)) {
        const u = usersByEmail.get(lower)!;
        return sanitizeProtocoloName(u.display_name || u.effective_name, "Sofia");
      }
      return sanitizeProtocoloName(senderName.trim(), "Sofia");
    }

    return "Patron";
  };

  return {
    users,
    usersById,
    usersByEmail,
    agentsByType,
    loading,
    getUserDisplayName,
    getAgentDisplayName,
    sanitizeProtocoloName,
    normalizeAgentType,
    resolveSenderDisplayName,
  };
}

